import React, { useState } from "react";

// Assuming tRPC client setup like this, adjust if necessary
import Input from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
// Import the main trpc object which includes hooks like useContext
import { trpc, trpcClient } from "@editor/utils/trpc";

import { toast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";

export function GenerateTemplatesForm() {
  const projectId = useCurrentProjectId();
  // Get tRPC utils context
  const utils = trpc.useContext();

  const [url, setUrl] = useState("");
  const [storeContext, setStoreContext] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Removed local error/success state in favor of toasts

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!url) {
      toast({
        header: "Error",
        message: "Please provide both a URL and store context.",
      });
      return;
    }

    try {
      new URL(url); // Validate URL format
    } catch {
      toast({
        header: "Error",
        message: "Invalid URL format.",
      });
      return;
    }

    setIsLoading(true);
    try {
      if (!projectId) {
        toast({
          header: "Error",
          message: "No project id",
        });
        setIsLoading(false); // Also stop loading here
        return;
      }

      await trpcClient.ai.generateComponentTemplatesFromUrl.query({
        url,
        projectId,
        storeContext,
      });
      toast({
        header: "Success",
        message: "Template generation started successfully!",
      });

      // Invalidate the reactFile.list query to trigger a refetch where it's used
      await utils.reactFile.list.invalidate({ projectId });

      // setUrl("");
      // setStoreContext("");
    } catch (error: any) {
      console.error("Failed to start template generation:", error);
      toast({
        header: "Error",
        message: `Failed to start generation: ${error.message || "Unknown error"}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="flex max-w-md flex-col gap-4 rounded-md border p-4"
    >
      <h2 className="text-lg font-semibold">Generate Page</h2>
      <div className="flex flex-col gap-1">
        <label htmlFor="url-input" className="text-sm font-medium">
          Store URL:
        </label>
        <Input
          id="url-input"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com"
          isRequired
        />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="context-textarea" className="text-sm font-medium">
          Store Context:
        </label>
        <Textarea
          id="context-textarea"
          value={storeContext}
          textLength="short"
          onChange={(value) => {
            setStoreContext(value);
          }}
          placeholder="Provide context about the store..."
        />
      </div>
      <Button type="submit" isLoading={isLoading} variant="primary">
        {isLoading ? "Starting Generation..." : "Generate Templates"}
      </Button>
      {/* Removed local error/success messages */}
    </form>
  );
}
